import { groupLifeProposalsService } from '@/services/proposals/group-life';
import type { GroupLifeCloneProposalResponse } from '@/services/proposals/group-life';
import {
  clearCloneIdempotencyKey,
  getCloneIdempotencyKey,
  GroupLifeClonePendingError,
  isClonePendingStatus,
} from '@/shared/utils/group-life-clone-idempotency.util';
import { toaster } from '@/shared/ui/toaster.component';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type Variables = {
  policyId: string;
};

/**
 * Clona a proposta PME em qualquer status, exceto transmitida ou com os dois
 * envelopes de assinatura concluídos. A clonada nasce em "Proposta em
 * Digitação" (6) com o plano bloqueado; vencimento, comissionamento e vidas
 * seguem editáveis.
 */
export function useCloneGroupLifeProposal() {
  const queryClient = useQueryClient();

  return useMutation<GroupLifeCloneProposalResponse, Error, Variables>({
    mutationKey: ['group-life-proposal', 'clone'],
    mutationFn: async ({ policyId }) => {
      const response = await groupLifeProposalsService.cloneProposal(
        policyId,
        getCloneIdempotencyKey(policyId, 'PROPOSAL')
      );
      if (!response.success || !response.data) {
        // Timeout do gateway / clone em andamento: a proposta pode já existir.
        // A chave fica guardada para a próxima tentativa não duplicar.
        if (isClonePendingStatus(response.status)) {
          throw new GroupLifeClonePendingError('PROPOSAL');
        }
        throw new Error(response.error || 'Erro ao clonar proposta');
      }
      clearCloneIdempotencyKey(policyId, 'PROPOSAL');
      return response.data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['proposal-analyses'] });
      if (data.warning) {
        toaster.create({
          type: 'info',
          title: 'Recálculo em andamento',
          description: data.warning,
        });
      }
    },
    onError: error => {
      if (error instanceof GroupLifeClonePendingError) {
        queryClient.invalidateQueries({ queryKey: ['proposals'] });
        queryClient.invalidateQueries({ queryKey: ['proposal-analyses'] });
      }
    },
  });
}
